import React, { useCallback, useMemo } from "react";

import {
  Flex,
  FlexProps,
  IconDownArrow,
  Link,
  ListItem,
} from "@catchoftheday/cg-components";
import { styled, themeGet } from "@catchoftheday/theme";

import {
  ItemPath,
  trackMenuClick,
  trackMenuImpression,
} from "../../utilities/analytics";
import { getAbsoluteUrl } from "../utilities";

const ExpanderChevron = styled(IconDownArrow)`
  position: absolute;
  transform: rotate(270deg);
  right: 18px;
  width: 10px;
  height: 5px;
  top: calc(50% - 2.5px);
`;

type ItemWrapperProps = FlexProps & {
  highlight?: boolean;
};

const ItemWrapper = styled(Flex)<ItemWrapperProps>`
  position: relative;
  margin: 0px 15px;
  height: 48px;
  border-bottom: 0.5px solid #d3d3d3;

  ${(props) =>
    props.highlight &&
    `
    padding: 0px 17px;
    margin: 0px;
    margin-top: -1px;
    height: 54px;
    border-top: ${props.borderTop || "4px solid"};
    border-bottom: ${props.borderBottom || "4px solid"};
    border-color: ${themeGet("colors.ui.borderColor")(props)};
  `}

  ${(props) =>
    !props.highlight &&
    `    &:last-child {
    border-bottom: none;
  }`}

  li,
  a {
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
  }

  a,
  a:hover,
  a:focus {
    color: #222222;
    text-decoration: none;
    outline: none;

    svg {
      color: #222222;
    }
  }
`;

export type MenuItemType = ItemWrapperProps & {
  url?: string;
  hasChildren?: boolean;
  shopAll?: boolean;
  itemPath?: ItemPath[];
  children: React.ReactNode;
  onClick?: () => void;
};

export const MenuItem = ({
  url,
  hasChildren,
  shopAll,
  highlight,
  itemPath,
  children,
  onClick,
  ...props
}: MenuItemType) => {
  const handleClick = useCallback(() => {
    if (typeof onClick === "function" && hasChildren) {
      onClick();
    }

    if (!itemPath) {
      return;
    }

    if (!hasChildren && url) {
      trackMenuClick({
        name: itemPath[itemPath.length - 1].name,
        url: getAbsoluteUrl(url),
        item_path: itemPath,
      });
      // Only track menu impressions for level 1
    } else if (itemPath.length === 1) {
      trackMenuImpression({
        name: itemPath[itemPath.length - 1].name,
        item_path: itemPath,
      });
    }
  }, [onClick, hasChildren, itemPath, url]);

  const renderListItem = useMemo(
    () => (
      <ListItem
        mb="0px"
        lineHeight="21px"
        fontSize="sm"
        fontWeight={shopAll ? "medium" : "light"}
        color={shopAll ? "secondary" : undefined}
        onClick={handleClick}
      >
        {children}
        {!shopAll && hasChildren && <ExpanderChevron />}
      </ListItem>
    ),
    [handleClick, hasChildren, shopAll, children]
  );

  const renderLinkItem = useMemo(
    () => <Link href={url}>{renderListItem}</Link>,
    [renderListItem, url]
  );

  return (
    <ItemWrapper highlight={highlight} {...props}>
      {url && !hasChildren ? renderLinkItem : renderListItem}
    </ItemWrapper>
  );
};
